import type { F6Threshold } from "./f6-visual-conformance.ts";

export interface F6ThresholdFitRow {
  readonly population:
    | "floor"
    | "positive-token-governed"
    | "positive-image-bearing"
    | "negative";
  readonly chromaticConformance: number;
  readonly discriminationMargin: number;
  readonly brandRecall: number;
  readonly judgeable: boolean;
}

export interface F6ThresholdFitResult {
  readonly separatedAxes: {
    readonly chromaticConformance: boolean;
    readonly discriminationMargin: boolean;
    readonly brandRecall: boolean;
  };
  readonly threshold: F6Threshold | null;
}

/**
 * Canonical F6 gap fitter. Every positive must clear every negative on every
 * axis; accepted thresholds are the four-decimal midpoint of those gaps.
 */
export function fitSeparatedF6Threshold(
  rows: readonly F6ThresholdFitRow[],
  calibrationRef: string,
  minChromaticFraction = 0.01,
): F6ThresholdFitResult {
  if (
    typeof calibrationRef !== "string" ||
    calibrationRef.length === 0 ||
    !Number.isFinite(minChromaticFraction) ||
    minChromaticFraction <= 0 ||
    minChromaticFraction > 1 ||
    rows.some((row) => !isFitRow(row))
  ) {
    throw new Error("f6-threshold-fit-input-invalid");
  }
  const judgeable = rows.filter((row) => row.judgeable);
  const positives = judgeable.filter(
    (row) =>
      row.population === "floor" ||
      row.population === "positive-token-governed",
  );
  const negatives = judgeable.filter((row) => row.population === "negative");
  const fitAxis = (read: (row: F6ThresholdFitRow) => number) => {
    if (positives.length === 0 || negatives.length === 0) return null;
    const positiveMin = Math.min(...positives.map(read));
    const negativeMax = Math.max(...negatives.map(read));
    if (positiveMin <= negativeMax) return null;
    const candidate = round4((positiveMin + negativeMax) / 2);
    // The portable threshold is four-decimal. A raw gap narrower than that
    // representation can round onto the negative or above the positive. Such
    // a candidate no longer separates the fitted population and must not ship.
    return candidate > negativeMax && candidate <= positiveMin
      ? candidate
      : null;
  };
  const chromaticConformance = fitAxis((row) => row.chromaticConformance);
  const discriminationMargin = fitAxis((row) => row.discriminationMargin);
  const brandRecall = fitAxis((row) => row.brandRecall);
  const separatedAxes = Object.freeze({
    chromaticConformance: chromaticConformance !== null,
    discriminationMargin: discriminationMargin !== null,
    brandRecall: brandRecall !== null,
  });
  return Object.freeze({
    separatedAxes,
    threshold:
      chromaticConformance !== null &&
      discriminationMargin !== null &&
      brandRecall !== null
        ? Object.freeze({
            minChromaticConformance: chromaticConformance,
            minBrandRecall: brandRecall,
            minDiscriminationMargin: discriminationMargin,
            minChromaticFraction,
            calibrationRef,
          })
        : null,
  });
}

function isFitRow(row: F6ThresholdFitRow): boolean {
  return (
    row !== null &&
    typeof row === "object" &&
    [
      "floor",
      "positive-token-governed",
      "positive-image-bearing",
      "negative",
    ].includes(row.population) &&
    [row.chromaticConformance, row.discriminationMargin, row.brandRecall].every(
      (value) => Number.isFinite(value) && value >= -1 && value <= 1,
    ) &&
    typeof row.judgeable === "boolean"
  );
}

function round4(value: number): number {
  return Number(value.toFixed(4));
}
